/**
 * Simonara — cookie / analytics consent (Option B, US-first).
 *
 * Honored per Standing Rules + the earlier Option B spec:
 *   - PostHog is NOT auto-initialized from index.html; this hook does it.
 *   - If localStorage says the visitor rejected analytics → do nothing.
 *   - If the browser signals GPC (navigator.globalPrivacyControl === true)
 *     → treat as rejected, no banner shown, PostHog never loads.
 *   - If no decision yet → PostHog initializes (notice-and-opt-out model),
 *     and the CookieBanner surfaces so the visitor can reject.
 *   - If the visitor rejects after accept → posthog.opt_out_capturing().
 *
 * Shirley's Standing Rule #1 keeps every change on PREVIEW only; nothing
 * in this file writes to production. The PostHog key below is the same
 * public write-key already shipped in index.html — this file does not
 * add new secrets.
 */

const STORAGE_KEY = "simonara_cookie_consent";
const POSTHOG_KEY = "phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs";
const POSTHOG_HOST = "https://us.i.posthog.com";

export const CONSENT_STATES = Object.freeze({
    ACCEPTED: "accepted",
    REJECTED: "rejected",
    UNKNOWN: null,
});

/* ----------- Low-level helpers ------------------------------------------ */

const hasGPC = () => {
    try {
        return typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;
    } catch (_err) {
        return false;
    }
};

export const readConsent = () => {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        if (v === "accepted" || v === "rejected") return v;
    } catch (_err) {}
    return null;
};

const writeConsent = (value) => {
    try {
        if (value === null) localStorage.removeItem(STORAGE_KEY);
        else localStorage.setItem(STORAGE_KEY, value);
    } catch (_err) {}
};

/* ----------- PostHog loader --------------------------------------------- */

let posthogLoaded = false;

const loadPostHog = () => {
    if (posthogLoaded) return;
    if (typeof window === "undefined") return;
    posthogLoaded = true;
    // Minimal queueing stub + async loader. Mirrors the official PostHog
    // snippet but is gated behind consent so it never fires without it.
    (function (t, e) {
        if (e.__SV) return;
        window.posthog = e;
        e._i = [];
        e.init = function (i, s, a) {
            function g(obj, name) {
                const parts = name.split(".");
                let target = obj;
                let key = name;
                if (parts.length === 2) {
                    target = obj[parts[0]];
                    key = parts[1];
                }
                target[key] = function () {
                    target.push([key].concat(Array.prototype.slice.call(arguments, 0)));
                };
            }
            const p = t.createElement("script");
            p.type = "text/javascript";
            p.crossOrigin = "anonymous";
            p.async = true;
            p.src = s.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") + "/static/array.js";
            const r = t.getElementsByTagName("script")[0];
            r.parentNode.insertBefore(p, r);
            let u = e;
            if (typeof a !== "undefined") u = e[a] = [];
            else a = "posthog";
            u.people = u.people || [];
            const methods = (
                "init capture identify alias set_config register register_once " +
                "unregister reset get_distinct_id getFeatureFlag isFeatureEnabled " +
                "reloadFeatureFlags onFeatureFlags opt_in_capturing opt_out_capturing " +
                "has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing"
            ).split(" ");
            for (let n = 0; n < methods.length; n++) g(u, methods[n]);
            e._i.push([i, s, a]);
        };
        e.__SV = 1;
    })(document, window.posthog || []);

    window.posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        person_profiles: "identified_only",
        session_recording: {
            recordCrossOriginIframes: true,
            capturePerformance: false,
        },
    });
};

const optOutPostHog = () => {
    try {
        if (window.posthog && typeof window.posthog.opt_out_capturing === "function") {
            window.posthog.opt_out_capturing();
        }
    } catch (_err) {}
};

/* ----------- Public API ------------------------------------------------- */

/**
 * Called once from App.js on mount. Reconciles consent state with
 * localStorage + GPC and either loads PostHog or opts it out.
 */
export const bootstrapConsent = () => {
    // GPC trumps anything stored locally. If a GPC-signalling browser
    // reaches us, treat the visitor as rejected regardless.
    if (hasGPC()) {
        writeConsent("rejected");
        optOutPostHog();
        return "rejected";
    }
    const current = readConsent();
    if (current === "rejected") {
        optOutPostHog();
        return "rejected";
    }
    // "accepted" OR unknown (first visit) → load PostHog. First-visit
    // tracking is permissible under CCPA notice-and-opt-out; the banner
    // surfaces to give the visitor an immediate reject path.
    loadPostHog();
    return current; // null for first visit (banner will show), else "accepted"
};

/**
 * Called by the banner's "Accept" button.
 */
export const acceptConsent = () => {
    writeConsent("accepted");
    loadPostHog();
    try {
        if (window.posthog && typeof window.posthog.opt_in_capturing === "function") {
            window.posthog.opt_in_capturing();
        }
    } catch (_err) {}
};

/**
 * Called by the banner's "Reject" button and by the "Do Not Sell or
 * Share" footer link.
 */
export const rejectConsent = () => {
    writeConsent("rejected");
    optOutPostHog();
};

/**
 * Called by the footer "Cookie preferences" button. Clears the stored
 * decision so the banner shows again (unless GPC is on, in which case
 * the banner stays hidden and rejection remains sticky).
 */
export const resetConsent = () => {
    if (hasGPC()) return;
    writeConsent(null);
};

export const isGPCActive = hasGPC;
